import type { FC } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import type { ListItemProps } from '@mui/material/ListItem'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'

type Direction = 'horizontal' | 'vertical'

interface PropertyListItemProps extends ListItemProps {
  isCategory?: boolean
  categoryStyle?: { value: string; key: string; label: string }
  align?: Direction
  label: string
  value?: string
}

export const PropertyListItem: FC<PropertyListItemProps> = (props) => {
  const {
    isCategory,
    categoryStyle,
    align = 'vertical',
    children,
    disableGutters,
    value,
    label,
    ...other
  } = props

  return (
    <ListItem
      sx={{
        px: disableGutters ? 0 : 3,
        py: 1.5
      }}
      {...other}
    >
      <ListItemText
        disableTypography
        primary={
          <Typography
            sx={{ minWidth: align === 'vertical' ? 'inherit' : 180 }}
            variant='subtitle2'
          >
            {label}
          </Typography>
        }
        secondary={
          <Box
            sx={{
              flex: 1,
              mt: align === 'vertical' ? 0.5 : 0
            }}
          >
            {children ||
              (isCategory ? (
                <Chip
                  label={value}
                  style={{
                    backgroundColor: categoryStyle?.value,
                    color: getContrastColor(categoryStyle?.value)
                  }}
                />
              ) : (
                <Typography
                  color='text.secondary'
                  variant='body2'
                >
                  {value}
                </Typography>
              ))}
          </Box>
        }
        sx={{
          display: 'flex',
          flexDirection: align === 'vertical' ? 'column' : 'row',
          my: 0
        }}
      />
    </ListItem>
  )
}

const getContrastColor = (bgColor) => {
  if (!bgColor) return '#000000' // Default to black if no color is provided

  // Convert hex to RGB
  const r = parseInt(bgColor.substring(1, 3), 16) / 255
  const g = parseInt(bgColor.substring(3, 5), 16) / 255
  const b = parseInt(bgColor.substring(5, 7), 16) / 255

  // Calculate luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

  // Return black or white based on brightness
  return luminance > 0.5 ? '#000000' : '#FFFFFF'
}
